/**
 * useFunnel Hook - Access funnel state and navigation methods
 *
 * This hook consumes the funnel state from TagadaProvider. All complex
 * initialization and session management is handled at the provider level.
 *
 * Usage:
 * ```tsx
 * // In your root component or layout:
 * <TagadaProvider funnelId="funnelv2_xxx" autoInitializeFunnel={true}>
 *   <YourApp />
 * </TagadaProvider>
 *
 * // In any child component:
 * const { context, next, isLoading, stepConfig } = useFunnel();
 *
 * // Access step-specific config (payment flows, resources, etc.)
 * const offerId = stepConfig.resources?.offer;
 * const paymentFlowId = stepConfig.paymentFlowId;
 * ```
 */
import { useMemo } from 'react';
import { TrackingProvider, getAssignedOrderBumpOfferIds, getAssignedPaymentFlowId, getAssignedPaymentInitiator, getAssignedPixels, getAssignedResources, getAssignedScripts, getAssignedStepConfig, getAssignedUpsellOfferIds, } from '../../core/funnelClient';
import { useTagadaContext } from '../providers/TagadaProvider';
/**
 * Hook to access funnel state and methods
 *
 * This hook returns the funnel state from TagadaProvider plus step configuration
 * from HTML injection. All complex logic is handled at the provider level.
 *
 * @returns FunnelContextValue with state, methods, and step config
 */
export function useFunnel() {
    const { funnel } = useTagadaContext();
    // Compute step config from HTML injection (memoized, computed once on mount)
    const stepConfig = useMemo(() => {
        const raw = getAssignedStepConfig();
        const resources = getAssignedResources();
        return {
            raw,
            paymentFlowId: getAssignedPaymentFlowId(),
            resources,
            staticResources: resources,
            pixels: getAssignedPixels(),
            getScripts: (position) => getAssignedScripts(position),
            orderBumpOfferIds: getAssignedOrderBumpOfferIds(),
            upsellOfferIds: getAssignedUpsellOfferIds(),
            paymentInitiator: getAssignedPaymentInitiator(),
        };
    }, []);
    return {
        ...funnel,
        stepConfig,
    };
}
